import { useCallback, useEffect, useState } from 'react';
import { Transition as HistoryTransition } from 'history';
import { useNavigate } from 'react-router-dom';

import useBlocker from '../../../hooks/useBlocker';

interface UseUnsavedChangesGuardOptions {
  hasUnsavedChanges: boolean;
  /** Block in-app navigation while true (typically `hasUnsavedChanges && !saving`). */
  blockNavigation: boolean;
  /** Where "Back" / discard-without-pending-navigation goes. */
  exitPath: string;
  /** Extra cleanup on discard (e.g. clearing the unsaved-changes baseline). */
  onDiscard?: () => void;
}

/**
 * Unsaved-changes apparatus shared by the template editors: blocks router
 * navigation, warns on browser-level exit (refresh / tab close), tracks the
 * pending transition, and drives UnsavedChangesModal.
 */
export const useUnsavedChangesGuard = ({
  hasUnsavedChanges,
  blockNavigation,
  exitPath,
  onDiscard,
}: UseUnsavedChangesGuardOptions) => {
  const navigate = useNavigate();
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] =
    useState(false);
  const [navTransition, setNavTransition] = useState<HistoryTransition>();

  const blocker = useCallback(
    (transition: HistoryTransition) => {
      if (!isUnsavedChangesModalOpen) {
        setNavTransition(transition);
        setIsUnsavedChangesModalOpen(true);
      }
    },
    [isUnsavedChangesModalOpen],
  );

  useBlocker(blocker, blockNavigation);

  // Native prompt for refresh / tab close / typed URL (router blocker can't).
  // Key off hasUnsavedChanges — not blockNavigation — so an in-flight save
  // (which clears blockNavigation) still warns if the user leaves mid-request.
  useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Chrome requires returnValue to be set for the prompt to show.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const goBack = () => {
    if (hasUnsavedChanges) {
      setNavTransition(undefined);
      setIsUnsavedChangesModalOpen(true);
      return;
    }
    navigate(exitPath);
  };

  const confirmDiscard = () => {
    setIsUnsavedChangesModalOpen(false);
    onDiscard?.();
    if (navTransition) {
      navTransition.retry();
      setNavTransition(undefined);
      return;
    }
    navigate(exitPath);
  };

  const closeUnsavedChangesModal = () => {
    setIsUnsavedChangesModalOpen(false);
    setNavTransition(undefined);
  };

  return {
    isUnsavedChangesModalOpen,
    closeUnsavedChangesModal,
    goBack,
    confirmDiscard,
  };
};
